import { useState } from 'react';
import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import { auditAction } from '@xdocs/schemas';
import type { AuditAction, AuditEvent } from '@xdocs/schemas';
import { Button, Checkbox, DataTable, Input, Select, cn, useI18n } from '@xdocs/ui';
import type { ColumnDef } from '@xdocs/ui';
import { RoleGate } from '../../../features/admin/index.js';
import { optionalProp } from '../../../lib/optionalProp.js';
import { AuditDetailPanel } from './AuditDetailPanel.js';
import type { AuditFilters } from './useAuditList.js';
import { useAuditList } from './useAuditList.js';
import { useAuditT } from './useAuditT.js';
import styles from './AuditPage.module.css';

const EMPTY_FILTERS: AuditFilters = {};

function parseDateInput(value: string): Date | undefined {
  return value === '' ? undefined : new Date(value);
}

/** Shallow field-by-field compare (Dates by epoch ms) — used to disable
 * "Apply filters" when the draft is identical to what's already applied, and
 * "Reset" when both draft and applied are already empty, so neither control
 * is ever a submit-to-same-state no-op (E15 dead-control audit would
 * otherwise flag it: setting state to its current value produces no
 * observable DOM change). */
function auditFiltersEqual(a: AuditFilters, b: AuditFilters): boolean {
  return (
    (a.action ?? '') === (b.action ?? '') &&
    (a.actorUserId ?? '') === (b.actorUserId ?? '') &&
    (a.targetType ?? '') === (b.targetType ?? '') &&
    (a.targetId ?? '') === (b.targetId ?? '') &&
    (a.impersonationOnly ?? false) === (b.impersonationOnly ?? false) &&
    (a.from?.getTime() ?? null) === (b.from?.getTime() ?? null) &&
    (a.to?.getTime() ?? null) === (b.to?.getTime() ?? null)
  );
}

/**
 * `/admin/audit` (F59) — read-only audit viewer on the shared `<DataTable>`
 * (E4 §8.3): filters by actor/action/target/date-range/impersonation-only,
 * row → detail with `metadataJsonb` before/after + reason. This viewer is
 * itself never an audited mutation (F59 AC).
 *
 * Wrapped in `<RoleGate minimum="admin">` (`features/admin/`, sibling M4
 * task) — F59 is `admin`-only; see `PromosPage.tsx`'s doc comment for the
 * defense-in-depth rationale (UX-only, server enforces on every op).
 */
export function AuditPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useAuditT();
  const [draftFilters, setDraftFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AuditFilters>(EMPTY_FILTERS);
  const [openEvent, setOpenEvent] = useState<AuditEvent | null>(null);

  const { rows, total, nextCursor, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry } =
    useAuditList(appliedFilters);

  const columns: ColumnDef<AuditEvent>[] = [
    {
      id: 'createdAt',
      header: t('adminAudit:list.column.timestamp'),
      sortable: true,
      primary: true,
      cell: (row) => <span className={cn(styles['metaCell'])}>{formatDate(locale, row.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}</span>,
    },
    {
      id: 'action',
      header: t('adminAudit:list.column.action'),
      cell: (row) => <span>{t(`adminAudit:action.${row.action}`)}</span>,
    },
    {
      id: 'target',
      header: t('adminAudit:list.column.target'),
      hideOnMobile: true,
      cell: (row) => (
        <span className={cn(styles['mono'])}>
          {row.targetType && row.targetId ? `${row.targetType}:${row.targetId}` : t('adminAudit:target.deleted')}
        </span>
      ),
    },
    {
      id: 'reason',
      header: t('adminAudit:list.column.reason'),
      hideOnMobile: true,
      cell: (row) => <span>{typeof row.metadataJsonb?.['reason'] === 'string' ? row.metadataJsonb['reason'] : t('adminAudit:reason.none')}</span>,
    },
    {
      id: 'actorUserId',
      header: t('adminAudit:list.column.actor'),
      cell: (row) => <span className={cn(styles['mono'])}>{row.actorUserId}</span>,
    },
  ];

  return (
    <RoleGate minimum="admin">
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('adminAudit:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('adminAudit:page.description')}</p>
      </div>

      <form
        className={cn(styles['filterBar'])}
        onSubmit={(event) => {
          event.preventDefault();
          setAppliedFilters(draftFilters);
        }}
      >
        <Select
          label={t('adminAudit:filter.action.label')}
          options={[{ value: '', label: t('adminAudit:filter.action.all') }, ...auditAction.options.map((a) => ({ value: a, label: t(`adminAudit:action.${a}`) }))]}
          value={draftFilters.action ?? ''}
          onChange={(event) =>
            setDraftFilters((prev) => ({ ...prev, action: (event.target.value || undefined) as AuditAction | undefined }))
          }
        />
        <Input
          label={t('adminAudit:filter.actor.label')}
          placeholder={t('adminAudit:filter.actor.placeholder')}
          value={draftFilters.actorUserId ?? ''}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, actorUserId: event.target.value || undefined }))}
        />
        <Input
          label={t('adminAudit:filter.target.label')}
          placeholder={t('adminAudit:filter.target.placeholder')}
          value={draftFilters.targetId ?? ''}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, targetId: event.target.value || undefined }))}
        />
        <Input
          type="date"
          label={t('adminAudit:filter.from.label')}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, from: parseDateInput(event.target.value) }))}
        />
        <Input
          type="date"
          label={t('adminAudit:filter.to.label')}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, to: parseDateInput(event.target.value) }))}
        />
        <Checkbox
          label={t('adminAudit:filter.impersonationOnly.label')}
          checked={draftFilters.impersonationOnly ?? false}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, impersonationOnly: event.target.checked }))}
        />
        <div className={cn(styles['filterActions'])}>
          <Button
            type="button"
            variant="ghost"
            disabled={auditFiltersEqual(draftFilters, EMPTY_FILTERS) && auditFiltersEqual(appliedFilters, EMPTY_FILTERS)}
            onClick={() => {
              setDraftFilters(EMPTY_FILTERS);
              setAppliedFilters(EMPTY_FILTERS);
            }}
          >
            {t('adminAudit:filter.reset')}
          </Button>
          <Button type="submit" variant="primary" disabled={auditFiltersEqual(draftFilters, appliedFilters)}>
            {t('adminAudit:filter.apply')}
          </Button>
        </div>
      </form>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        query={query}
        onQueryChange={onQueryChange}
        onRowClick={(row) => setOpenEvent(row)}
        label={t('adminAudit:page.title')}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        total={total}
        nextCursor={nextCursor}
        onLoadMore={onLoadMore}
        isLoadingMore={isLoadingMore}
        {...optionalProp('loadMoreError', loadMoreError)}
        emptyTitle={t('adminAudit:list.empty.title')}
        emptyDescription={t('adminAudit:list.empty.description')}
        noResultsTitle={t('adminAudit:list.noResults.title')}
        onClearFilters={() => {
          setDraftFilters(EMPTY_FILTERS);
          setAppliedFilters(EMPTY_FILTERS);
        }}
        searchLabel={t('adminAudit:list.searchLabel')}
        searchPlaceholder={t('adminAudit:list.searchPlaceholder')}
      />

      {openEvent && <AuditDetailPanel event={openEvent} onClose={() => setOpenEvent(null)} />}
    </div>
    </RoleGate>
  );
}
